import { memo } from 'react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { VStack } from '@library';
import type { CategoryFilter } from '../../types';
import useCurrentCategoryFilters from '../../queries/useCurrentCategoryFilters';
import LazyComponent from '../../../core/components/router/LazyComponent';
import { SmallProgressIndicator } from '../../../core/components/ProgressIndicators';
import useStoreAndLanguageParams from '../../../core/hooks/useStoreAndLanguageParams';
import withErrorBoundary from '../../../core/components/errors/withErrorBoundary';
import RestrictedContent from '../../../core/components/router/RestrictedContent';
import useCategory from '../../../core/queries/useCategory';
import styles from './AttributeFilter.module.scss';
import Filter from './Filter';
import FilterSize from './FilterSize';
import FilterPrice from './FilterPrice';
import FilterColor from './FilterColor';
import FilterTyre from './FilterTyre';
import FilterSearch from './FilterSearch';
import FilterBrand from './FilterBrand';
import FilterSaleAndNew from './FilterSaleAndNew';
import FilterAvailability from './FilterAvailability';

const FilterVehicle = LazyComponent(async () => import('./FilterVehicle'));

export interface AttributeFilterValues {
    name?: string;
    values?: string[];
}

const useCategoryFilters = () => {
    const { category } = useCurrentCategoryFilters();
    const { categoryId } = useCategory();
    const hasMotorcycleFilter =
        !!category.filters.filter(
            f => f.field === 'PIM_ATT_WEB_SHOW_MOTORCYCLE_SEARCH'
        ).length || categoryId === '1783657';

    return {
        categoryExists: !!category,
        originalFilters: category.filters,
        hasMotorcycleFilter,
    };
};

const AttributeFilter = ({
    ignoreList = [],
    salePage,
    priceRange,
    defaultExpand,
    children,
    isSearchResultPage = false,
}: {
    ignoreList?: string[];
    salePage?: boolean;
    priceRange: { min: number; max: number; loading: boolean };
    defaultExpand?: boolean;
    children?: ReactNode;
    isSearchResultPage?: boolean;
}) => {
    const { language } = useStoreAndLanguageParams();
    const intl = useIntl();
    const { categoryExists, originalFilters, hasMotorcycleFilter } =
        useCategoryFilters();

    if (!categoryExists) {
        return null;
    }

    let filters = originalFilters;
    if (isSearchResultPage) {
        filters = [
            {
                id: `${language}PIM_ATT_ERP_MANUFACTURER`,
                name: intl.formatMessage({ id: 'make' }),
                field: 'PIM_ATT_ERP_MANUFACTURER',
                values: [],
            },
            ...filters.filter(
                item => item.field !== 'PIM_ATT_ERP_MANUFACTURER'
            ),
        ];
    }

    return (
        <div className={styles.attributeFilters}>
            <VStack gap="medium">
                {hasMotorcycleFilter && !ignoreList.includes('vehicleId') && (
                    <div className={styles.filter}>
                        <FilterVehicle defaultExpand={defaultExpand} />
                    </div>
                )}
                {!ignoreList.includes('q') && (
                    <div className={styles.filter}>
                        <FilterSearch
                            name={intl.formatMessage({
                                id: 'search',
                            })}
                            defaultExpand={defaultExpand}
                        />
                    </div>
                )}
                <RestrictedContent featureFlag="availabilityFilter">
                    <div className={styles.filter}>
                        <FilterAvailability defaultExpand={defaultExpand} />
                    </div>
                </RestrictedContent>
                {!salePage && (
                    <div className={styles.filter}>
                        <FilterSaleAndNew defaultExpand={defaultExpand} />
                    </div>
                )}
                {children}

                {filters
                    .filter(filter => !ignoreList.includes(filter.field))
                    .sort((a, b) => {
                        if (a.field.includes('MATCHCODE')) return -1;
                        if (b.field.includes('MATCHCODE')) return 1;
                        return 0;
                    })
                    .map(filter => (
                        <AttributeFilterItem
                            defaultExpand={defaultExpand}
                            key={filter.id}
                            filter={filter}
                        />
                    ))}

                {priceRange.loading ? (
                    <SmallProgressIndicator />
                ) : (
                    <div className={styles.filter}>
                        <FilterPrice
                            priceRange={priceRange}
                            name={intl.formatMessage({
                                id: 'price',
                            })}
                            defaultExpand={defaultExpand}
                        />
                    </div>
                )}
            </VStack>
        </div>
    );
};

const AttributeFilterItem = memo(
    ({
        filter,
        defaultExpand,
    }: {
        filter: CategoryFilter;
        defaultExpand?: boolean;
    }) => {
        const intl = useIntl();

        if (filter.field === 'PIM_ATT_MATCHCODE') {
            return (
                <div className={styles.filter}>
                    <FilterTyre
                        defaultExpand={defaultExpand}
                        name={intl.formatMessage({
                            id: 'match-code',
                        })}
                    />
                </div>
            );
        }

        if (filter.field === 'PIM_ATT_COLOUR_WEB') {
            return (
                <div className={styles.filter}>
                    <FilterColor
                        defaultExpand={defaultExpand}
                        name={filter.name}
                        field={filter.field}
                    />
                </div>
            );
        }

        if (
            filter.field === 'PIM_ATT_SIZE' ||
            filter.field === 'PIM_ATT_SIZE_COMBINATION'
        ) {
            return (
                <div className={styles.filter}>
                    <FilterSize
                        defaultExpand={defaultExpand}
                        name={filter.name}
                        field={filter.field}
                    />
                </div>
            );
        }

        if (filter.field === 'PIM_ATT_ERP_MANUFACTURER') {
            return (
                <div className={styles.filter}>
                    <FilterBrand
                        defaultExpand={defaultExpand}
                        name={filter.name}
                        field={filter.field}
                    />
                </div>
            );
        }
        return (
            <div className={styles.filter}>
                <Filter
                    defaultExpand={defaultExpand}
                    name={filter.name}
                    field={filter.field}
                />
            </div>
        );
    }
);

export default memo(withErrorBoundary(AttributeFilter));
